import React, { useCallback, useRef, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Connection,
  NodeTypes,
  EdgeTypes,
  ReactFlowProvider,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Card, Button, Typography, Space, Tag, Alert, Modal, Input, Select, App, Tooltip } from 'antd';
import { 
  PlayCircleOutlined, 
  SaveOutlined,
  LoadingOutlined,
  BugOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  DeleteOutlined,
  BarChartOutlined
} from '@ant-design/icons';

import LLMNode from './nodes/LLMNode';
import ToolNode from './nodes/ToolNode';
import ConditionNode from './nodes/ConditionNode';
import StartNode from './nodes/StartNode';
import EndNode from './nodes/EndNode';
import InputNode from './nodes/InputNode';
import SQLQueryNode from './nodes/SQLQueryNode';
import HTTPRequestNode from './nodes/HTTPRequestNode';
import NodePalette from './NodePalette';
import ExecutionPanel from './ExecutionPanel';
import NodeConfigurationModal from './NodeConfigurationModal';
import { apiUtils } from '../../lib/api';

const { Text } = Typography;

// Node Types
const nodeTypes: NodeTypes = {
  start: StartNode,
  end: EndNode,
  input: InputNode,
  llm: LLMNode,
  tool: ToolNode,
  condition: ConditionNode,
  'sql-query': SQLQueryNode,
  'http-request': HTTPRequestNode,
};

// Edge Types with custom styling
const edgeTypes: EdgeTypes = {};

// Empty initial nodes for new graphs
const emptyNodes: Node[] = [];

// Empty initial edges for new graphs
const emptyEdges: Edge[] = [];



interface StateGraphEditorProps {
  projectId?: string;
  readonly?: boolean;
  agentId?: string | null;
}

const StateGraphEditor: React.FC<StateGraphEditorProps> = ({ 
  projectId, 
  readonly = false,
  agentId = null
}) => {
  const { message: messageApi } = App.useApp();
  const [nodes, setNodes, onNodesChange] = useNodesState(emptyNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(emptyEdges);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResults, setExecutionResults] = useState<any>(null);
  const [savedGraphs, setSavedGraphs] = useState<any[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [saveGraphName, setSaveGraphName] = useState('');
  const [saveGraphDescription, setSaveGraphDescription] = useState('');
  const [selectedGraphId, setSelectedGraphId] = useState<string>('');
  const [currentGraphId, setCurrentGraphId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showPalette, setShowPalette] = useState(false);
  const [showExecution, setShowExecution] = useState(false);
  const [executionHistory, setExecutionHistory] = useState<any[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configNode, setConfigNode] = useState<Node | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [showInputModal, setShowInputModal] = useState(false);
  const [inputCollectionData, setInputCollectionData] = useState<{nodeId: string, label: string, description?: string, placeholder?: string}[]>([]);
  const [inputValues, setInputValues] = useState<{[nodeId: string]: string}>({});
  const [isExecutionAnimationComplete, setIsExecutionAnimationComplete] = useState(false);

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);

  // Load saved graphs when component mounts
  React.useEffect(() => {
    if (projectId) {
      loadSavedGraphs();
    }
  }, [projectId]);

  // Load specific agent when agentId is provided
  React.useEffect(() => {
    if (agentId && projectId) {
      loadSpecificAgent(agentId);
    } else if (agentId === null) {
      // Create new agent - reset to empty state
      setNodes(emptyNodes);
      setEdges(emptyEdges);
      setCurrentGraphId(null);
      setHasUnsavedChanges(false);
    }
  }, [agentId, projectId]);

  // Delete selected nodes
  const deleteSelectedNodes = useCallback(() => {
    if (selectedNodes.length === 0) return;

    // Remove nodes
    setNodes(nds => nds.filter(node => !selectedNodes.includes(node.id)));
    
    // Remove edges connected to deleted nodes
    setEdges(eds => eds.filter(edge => 
      !selectedNodes.includes(edge.source) && !selectedNodes.includes(edge.target)
    ));
    
    setSelectedNodes([]);
    setSelectedNode(null);
    setHasUnsavedChanges(true);
    messageApi.success(`Deleted ${selectedNodes.length} node(s)`);
  }, [selectedNodes, setNodes, setEdges]);

  // Delete individual node with confirmation
  const deleteNode = useCallback((nodeId: string, nodeLabel: string) => {
    Modal.confirm({
      title: 'Delete Node',
      content: `Are you sure you want to delete "${nodeLabel}"?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        // Remove the node
        setNodes(nds => nds.filter(node => node.id !== nodeId));
        
        // Remove edges connected to this node
        setEdges(eds => eds.filter(edge => 
          edge.source !== nodeId && edge.target !== nodeId
        ));
        
        // Clear selection if this node was selected
        setSelectedNodes(prev => prev.filter(id => id !== nodeId));
        if (selectedNode?.id === nodeId) {
          setSelectedNode(null);
        }
        
        setHasUnsavedChanges(true);
        messageApi.success(`Node "${nodeLabel}" deleted`);
      }
    });
  }, [setNodes, setEdges, selectedNode]);

  // Prevent accidental page reload with unsaved changes and add keyboard shortcuts
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        return '';
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!readonly) {
          if (currentGraphId) {
            saveGraph();
          } else {
            showSaveDialog();
          }
        }
      }
      
      // Delete key to delete selected nodes
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (!readonly && selectedNodes.length > 0) {
          deleteSelectedNodes();
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasUnsavedChanges, readonly, selectedNodes.length, deleteSelectedNodes]);

  // Load saved graphs from backend
  const loadSavedGraphs = async () => {
    if (!projectId) return;
    
    try {
      const response = await apiUtils.backend.get(`/projects/${projectId}/graphs`);
      setSavedGraphs(response.data);
    } catch (error) {
      console.error('Failed to load saved graphs:', error);
      messageApi.error('Failed to load saved graphs');
    }
  };

  // Load specific agent for editing
  const loadSpecificAgent = async (graphId: string) => {
    if (!projectId || !graphId) return;

    setIsLoading(true);
    try {
      const response = await apiUtils.backend.get(`/projects/${projectId}/graphs/${graphId}`);
      const graphData = response.data;

      // Ensure nodes have proper structure with positions
      const loadedNodes = (graphData.nodes || []).map((node: any) => ({
        id: node.id,
        type: node.type,
        position: node.position || { x: 0, y: 0 }, // Ensure position exists
        data: node.data || {}
      }));

      // Ensure edges have proper structure
      const loadedEdges = (graphData.edges || []).map((edge: any) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        label: edge.label,
        data: edge.data || {}
      }));

      setNodes(loadedNodes);
      setEdges(loadedEdges);
      setCurrentGraphId(graphData.id);
      setHasUnsavedChanges(false);

      // Restore viewport if available
      if (graphData.metadata?.viewport && reactFlowInstance) {
        reactFlowInstance.setViewport(graphData.metadata.viewport);
      }

      messageApi.success(`Agent "${graphData.name}" loaded for editing`);
    } catch (error) {
      console.error('Failed to load agent:', error);
      messageApi.error('Failed to load agent for editing');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle new connections
  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) => addEdge(params, eds));
      setHasUnsavedChanges(true);
    },
    [setEdges]
  );

  // Custom nodes change handler to track dragging state
  const handleNodesChange = useCallback((changes: any[]) => {
    const isDraggingChange = changes.some(change => 
      change.type === 'position' && change.dragging
    );
    const isEndDragChange = changes.some(change => 
      change.type === 'position' && change.dragging === false
    );
    
    if (isDraggingChange && !isDragging) {
      setIsDragging(true);
    } else if (isEndDragChange && isDragging) {
      setIsDragging(false);
      setHasUnsavedChanges(true);
    }
    
    onNodesChange(changes);
  }, [onNodesChange, isDragging]);

  // Custom edges change handler
  const handleEdgesChange = useCallback((changes: any[]) => {
    onEdgesChange(changes);
    setHasUnsavedChanges(true);
  }, [onEdgesChange]);

  // Handle drag enter
  const onDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    // Add visual feedback
    if (reactFlowWrapper.current) {
      reactFlowWrapper.current.style.backgroundColor = 'rgba(24, 144, 255, 0.1)';
      reactFlowWrapper.current.style.border = '2px dashed #1890ff';
    }
  }, []);

  // Handle drag leave
  const onDragLeave = useCallback(() => {
    // Remove visual feedback
    if (reactFlowWrapper.current) {
      reactFlowWrapper.current.style.backgroundColor = '';
      reactFlowWrapper.current.style.border = '';
    }
  }, []);

  // Handle drag over
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop to add new nodes
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const dragData = event.dataTransfer.getData('application/reactflow');

      if (typeof dragData === 'undefined' || !dragData || !reactFlowBounds) {
        return;
      }

      if (!reactFlowInstance) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      let nodeData;
      let nodeType;
      
      try {
        // Try to parse as JSON first (for presets)
        const parsedData = JSON.parse(dragData);
        nodeType = parsedData.type;
        nodeData = parsedData.data || getDefaultNodeData(nodeType);
      } catch (parseError) {
        // If parsing fails, treat as simple node type string
        nodeType = dragData;
        nodeData = getDefaultNodeData(nodeType);
      }

      const newNode: Node = {
        id: `${nodeType}-${Date.now()}`,
        type: nodeType,
        position,
        data: nodeData,
      };
      
      setNodes((nds) => nds.concat(newNode));
      setHasUnsavedChanges(true);
      
      // Clean up visual feedback
      if (reactFlowWrapper.current) {
        reactFlowWrapper.current.style.backgroundColor = '';
        reactFlowWrapper.current.style.border = '';
      }
    },
    [reactFlowInstance, setNodes]
  );

  // Get default data for node types
  const getDefaultNodeData = (type: string) => {
    switch (type) {
      case 'start':
        return {
          label: 'Start',
          description: 'Entry point for the workflow'
        };
      case 'end':
        return {
          label: 'End',
          description: 'Exit point for the workflow'
        };
      case 'input':
        return {
          label: 'User Input',
          placeholder: 'Enter your question here...',
          description: 'Captures user input for the workflow'
        };
      case 'llm':
        return {
          label: 'New LLM Node',
          model: 'gpt-4-turbo-preview',
          description: 'LLM processing node',
          config: {
            temperature: 0.7,
            maxTokens: 1000,
            systemPrompt: ''
          }
        };
      case 'tool':
        return {
          label: 'Web Search Tool',
          tool: 'webSearch',
          description: 'Search the web for information',
          config: {
            maxResults: 10
          }
        };
      case 'condition':
        return {
          label: 'New Condition',
          condition: 'value > threshold',
          description: 'Conditional routing node'
        };
      case 'sql-query':
        return {
          label: 'SQL Query',
          description: 'Execute SQL queries on connected databases',
          dataSourceId: '',
          query: 'SELECT * FROM table_name WHERE condition = {{variable}}',
          outputFormat: 'json',
          resultLimit: 100,
          timeout: 30000
        };
      case 'http-request':
        return {
          label: 'HTTP Request',
          description: 'Make HTTP API calls to external services',
          url: 'https://api.example.com/data',
          method: 'GET',
          headers: {},
          responseFormat: 'json',
          timeout: 30000
        };
      default:
        return { label: `New ${type} Node` };
    }
  };

  // Execute the state graph
  const executeGraph = async () => {
    if (!currentGraphId || !projectId) {
      // If no saved graph, save it first
      if (!currentGraphId) {
        messageApi.warning('Please save the graph first before executing');
        return;
      }
    }

    // Check if there are input nodes in the graph
    const inputNodes = nodes.filter(node => node.type === 'input');

    if (inputNodes.length > 0) {
      // Show modal for input collection
      const inputData = inputNodes.map(node => ({
        nodeId: node.id,
        label: node.data.label || 'Input',
        description: node.data.description,
        placeholder: node.data.placeholder || 'Enter your input here...'
      }));
      
      setInputCollectionData(inputData);
      
      // Initialize input values
      const initialValues: {[nodeId: string]: string} = {};
      inputData.forEach(input => {
        initialValues[input.nodeId] = '';
      });
      setInputValues(initialValues);
      
      setShowInputModal(true);
      return;
    } else {
      // Fallback to modal input if no input nodes
      const fallbackInputData = [{
        nodeId: 'fallback-input',
        label: 'Workflow Input',
        description: 'Enter the input for your AI workflow',
        placeholder: 'What is artificial intelligence?'
      }];
      
      setInputCollectionData(fallbackInputData);
      setInputValues({ 'fallback-input': '' });
      setShowInputModal(true);
      return;
    }
  };

  // Continue execution with collected input
  const executeWithInput = async (userInput: string) => {
    setIsExecuting(true);
    setIsExecutionAnimationComplete(false); // Reset animation completion state
    // Only auto-show execution panel if it was previously visible or if user hasn't explicitly hidden it
    if (!executionResults) {
      setShowExecution(true);
    }
    
    try {
      let graphIdToExecute = currentGraphId;
      
      // If no current graph ID, save the graph first
      if (!graphIdToExecute) {
        const tempGraphData = {
          name: `Temp Graph ${Date.now()}`,
          description: 'Temporary graph for execution',
          nodes,
          edges,
          metadata: {
            viewport: reactFlowInstance?.getViewport(),
            created: new Date().toISOString()
          }
        };

        const saveResponse = await apiUtils.backend.post(`/projects/${projectId}/graphs`, tempGraphData);
        graphIdToExecute = saveResponse.data.id;
        setCurrentGraphId(graphIdToExecute);
      }

      // Execute the graph using our new API
      const response = await apiUtils.backend.post(`/projects/${projectId}/graphs/${graphIdToExecute}/execute`, {
        input: userInput,
        initialState: {
          confidence: 0.0,
          metadata: {
            startTime: new Date().toISOString()
          }
        }
      });

      const result = response.data;
      setExecutionResults(result);
      setExecutionHistory(prev => [...prev, {
        timestamp: new Date(),
        result,
        input: userInput
      }]);

      // Animate execution flow
      if (result.success) {
        if (result.executionPath && result.executionPath.length > 0) {
          animateExecution(result.executionPath);
        } else {
          // If no execution path, mark animation as complete immediately
          setIsExecutionAnimationComplete(true);
        }
        
        // Update execution stats
        if (graphIdToExecute && projectId) {
          try {
            await apiUtils.backend.put(`/projects/${projectId}/graphs/${graphIdToExecute}/execution-stats`);
          } catch (error) {
            console.error('Failed to update execution stats:', error);
          }
        }
      }

    } catch (error) {
      console.error('Execution failed:', error);
      setExecutionResults({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        finalState: { input: userInput, output: 'Execution failed' },
        executionPath: [],
        timings: {}
      });
    } finally {
      setIsExecuting(false);
    }
  };

  // Handle input modal submission
  const handleInputSubmit = () => {
    // Validate that all inputs have values
    const hasEmptyInputs = inputCollectionData.some(input => !inputValues[input.nodeId]?.trim());
    
    if (hasEmptyInputs) {
      messageApi.error('Please fill in all required inputs');
      return;
    }

    // Use the first input as primary input
    const primaryInput = inputValues[inputCollectionData[0]?.nodeId] || '';
    
    setShowInputModal(false);
    executeWithInput(primaryInput);
  };

  // Handle input modal cancellation
  const handleInputCancel = () => {
    setShowInputModal(false);
    setInputCollectionData([]);
    setInputValues({});
  };

  // Animate the execution flow
  const animateExecution = (executionPath: string[]) => {
    if (!executionPath || executionPath.length === 0) return;

    // Animate each step
    executionPath.forEach((nodeId, index) => {
      setTimeout(() => {
        setNodes(nds => nds.map(node => ({
          ...node,
          data: {
            ...node.data,
            isExecuting: node.id === nodeId,
            isCompleted: executionPath.slice(0, index).includes(node.id)
          }
        })));
      }, index * 1000);
    });

    // Reset animation after completion
    setTimeout(() => {
      setNodes(nds => nds.map(node => ({
        ...node,
        data: {
          ...node.data,
          isExecuting: false,
          isCompleted: true
        }
      })));
      // Mark animation as complete to show success dialog
      setIsExecutionAnimationComplete(true);
    }, executionPath.length * 1000 + 2000);
  };

  // Handle save button click
  const handleSaveClick = useCallback(() => {
    if (currentGraphId) {
      // Auto-save existing graph without modal
      saveGraph();
    } else {
      // Show modal for new graph
      showSaveDialog();
    }
  }, [currentGraphId]);

  // Show save modal (only for new graphs)
  const showSaveDialog = () => {
    setSaveGraphName('');
    setSaveGraphDescription('');
    setShowSaveModal(true);
  };

  // Save graph configuration
  const saveGraph = async (forceName?: string, forceDescription?: string) => {
    if (!projectId) {
      messageApi.error('No project selected');
      return;
    }

    // For new graphs, require name
    if (!currentGraphId && (!saveGraphName.trim() && !forceName)) {
      messageApi.error('Please provide a graph name');
      return;
    }

    setIsLoading(true);
    try {
      const graphConfig = {
        name: forceName || saveGraphName.trim() || savedGraphs.find(g => g.id === currentGraphId)?.name || 'Untitled Graph',
        description: forceDescription || saveGraphDescription.trim() || savedGraphs.find(g => g.id === currentGraphId)?.description || '',
        nodes: nodes.map(node => ({
          id: node.id,
          type: node.type,
          position: node.position, // Ensure position is included
          data: node.data
        })),
        edges: edges.map(edge => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
          label: edge.label,
          data: edge.data
        })),
        metadata: {
          viewport: reactFlowInstance?.getViewport(),
          lastSaved: new Date().toISOString(),
          nodeCount: nodes.length,
          edgeCount: edges.length
        }
      };

      console.log('[FRONTEND] Saving graph config:', JSON.stringify({
        name: graphConfig.name,
        nodeCount: graphConfig.nodes.length,
        nodesWithCustomCode: graphConfig.nodes.filter(n => n.data?.customCode).length,
        nodeDetails: graphConfig.nodes.map(n => ({
          id: n.id,
          type: n.type,
          hasCustomCode: !!(n.data?.customCode),
          customCodeLength: n.data?.customCode?.length || 0
        }))
      }, null, 2));

      let response;
      if (currentGraphId) {
        // Update existing graph
        response = await apiUtils.backend.put(`/projects/${projectId}/graphs/${currentGraphId}`, graphConfig);
        messageApi.success('Graph saved successfully');
      } else {
        // Create new graph
        response = await apiUtils.backend.post(`/projects/${projectId}/graphs`, graphConfig);
        messageApi.success('Graph created successfully');
        setCurrentGraphId(response.data.id);
      }

      setShowSaveModal(false);
      setSaveGraphName('');
      setSaveGraphDescription('');
      setHasUnsavedChanges(false);
      loadSavedGraphs();
    } catch (error) {
      console.error('Failed to save graph:', error);
      messageApi.error('Failed to save graph');
    } finally {
      setIsLoading(false);
    }
  };

  // Load graph configuration
  const loadGraph = async () => {
    if (!projectId || !selectedGraphId) {
      messageApi.error('Please select a graph to load');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiUtils.backend.get(`/projects/${projectId}/graphs/${selectedGraphId}`);
      const graphData = response.data;

      // Ensure nodes have proper structure with positions
      const loadedNodes = (graphData.nodes || []).map((node: any) => ({
        id: node.id,
        type: node.type,
        position: node.position || { x: 0, y: 0 }, // Ensure position exists
        data: node.data || {}
      }));

      // Ensure edges have proper structure
      const loadedEdges = (graphData.edges || []).map((edge: any) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        label: edge.label,
        data: edge.data || {}
      }));

      setNodes(loadedNodes);
      setEdges(loadedEdges);
      setCurrentGraphId(graphData.id);
      setHasUnsavedChanges(false);

      // Restore viewport if available
      if (graphData.metadata?.viewport && reactFlowInstance) {
        reactFlowInstance.setViewport(graphData.metadata.viewport);
      }

      setShowLoadModal(false);
      setSelectedGraphId('');
      messageApi.success(`Graph "${graphData.name}" loaded successfully`);
    } catch (error) {
      console.error('Failed to load graph:', error);
      messageApi.error('Failed to load graph');
    } finally {
      setIsLoading(false);
    }
  };

  // Create new graph
  const createNewGraph = () => {
    setNodes(emptyNodes);
    setEdges(emptyEdges);
    setCurrentGraphId(null);
    setHasUnsavedChanges(false);
    messageApi.success('New graph created');
  };

  // Delete graph
  const deleteGraph = async (graphId: string) => {
    if (!projectId) return;

    try {
      await apiUtils.backend.delete(`/projects/${projectId}/graphs/${graphId}`);
      if (currentGraphId === graphId) {
        createNewGraph();
      }
      loadSavedGraphs();
      messageApi.success('Graph deleted successfully');
    } catch (error) {
      console.error('Failed to delete graph:', error);
      messageApi.error('Failed to delete graph');
    }
  };

  // Node click handler
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    
    // Handle multi-selection with Ctrl/Cmd key
    if (event.ctrlKey || event.metaKey) {
      setSelectedNodes(prev => {
        if (prev.includes(node.id)) {
          return prev.filter(id => id !== node.id);
        } else {
          return [...prev, node.id];
        }
      });
    } else {
      setSelectedNodes([node.id]);
    }
  }, []);

  // Node double click handler for configuration
  const onNodeDoubleClick = useCallback((_event: React.MouseEvent, node: Node) => {
    if (readonly) return;
    setConfigNode(node);
    setShowConfigModal(true);
  }, [readonly]);

  // Handle node configuration save
  const handleNodeConfigSave = useCallback((nodeId: string, newData: any) => {
    setNodes(nds => nds.map(node => 
      node.id === nodeId 
        ? { ...node, data: newData }
        : node
    ));
    setHasUnsavedChanges(true);
    
    // Check if custom code was added and show appropriate message
    if (newData.customCode) {
      messageApi.warning('Node configuration saved. Please save the graph to persist changes!', 6);
    } else {
      messageApi.success('Node configuration updated');
    }
  }, [setNodes]);

  // Check if node is unlinked (not connected to any edges)
  const isNodeUnlinked = useCallback((nodeId: string) => {
    return !edges.some(edge => edge.source === nodeId || edge.target === nodeId);
  }, [edges]);

  // Handle pane click to clear selection
  const onPaneClick = useCallback(() => {
    setSelectedNodes([]);
    setSelectedNode(null);
  }, []);

  return (
    <div style={{ 
      height: '100%', 
      width: '100%',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <ReactFlowProvider>
        <div className="flex h-full" style={{ position: 'relative', zIndex: 1 }}>
          {/* Node Palette */}
          <NodePalette 
            visible={showPalette}
            onClose={() => setShowPalette(false)}
          />

          {/* Main Graph Area */}
          <div 
            className="flex-1" 
            ref={reactFlowWrapper}
            style={{ 
              position: 'relative',
              zIndex: 1,
              minHeight: '400px'
            }}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
          >
            <ReactFlow
              nodes={nodes.map(node => ({
                ...node,
                selected: selectedNodes.includes(node.id),
                data: {
                  ...node.data,
                  onDelete: readonly ? undefined : () => deleteNode(node.id, node.data.label || 'Node'),
                  isUnlinked: isNodeUnlinked(node.id),
                  hasCustomCode: !!(node.data.customCode && node.data.customCode.trim().length > 0)
                },
                style: {
                  ...node.style,
                  border: selectedNodes.includes(node.id)
                    ? '2px solid #1890ff'
                    : undefined
                }
              }))}
              edges={edges}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onConnect={onConnect}
              onInit={setReactFlowInstance}
              onNodeClick={onNodeClick}
              onNodeDoubleClick={onNodeDoubleClick}
              onPaneClick={onPaneClick}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              attributionPosition="top-right"
              style={{ height: '100%', width: '100%' }}
            >
              <Controls />
              <MiniMap />
              <Background color="#aaa" gap={16} />
              
              {/* Simple Controls Panel */}
              <Panel position="top-left">
                <Space>
                  <Button
                    type="primary"
                    icon={isExecuting ? <LoadingOutlined /> : <PlayCircleOutlined />}
                    onClick={executeGraph}
                    loading={isExecuting}
                    disabled={readonly}
                    size="small"
                  >
                    {isExecuting ? 'Executing...' : 'Execute'}
                  </Button>
                  
                  <Tooltip title="Save graph (Ctrl+S)">
                    <Button
                      icon={<SaveOutlined />}
                      onClick={handleSaveClick}
                      disabled={readonly}
                      size="small"
                      type={hasUnsavedChanges ? "primary" : "default"}
                      style={hasUnsavedChanges ? { 
                        backgroundColor: '#ff7a00', 
                        borderColor: '#ff7a00' 
                      } : {}}
                    >
                      {hasUnsavedChanges ? 'Save*' : currentGraphId ? 'Save' : 'Save As...'}
                    </Button>
                  </Tooltip>

                  <Button
                    icon={<FolderOpenOutlined />}
                    onClick={() => setShowLoadModal(true)}
                    disabled={readonly}
                    size="small"
                  >
                    Load
                  </Button>

                  <Button
                    icon={<PlusOutlined />}
                    onClick={createNewGraph}
                    disabled={readonly}
                    size="small"
                  >
                    New
                  </Button>
                  
                  <Button
                    icon={<BugOutlined />}
                    onClick={() => setShowPalette(true)}
                    disabled={readonly}
                    size="small"
                    type={showPalette ? "primary" : "default"}
                  >
                    Add Nodes
                  </Button>
                  
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={deleteSelectedNodes}
                    disabled={readonly || selectedNodes.length === 0}
                    size="small"
                    danger
                  >
                    Delete ({selectedNodes.length})
                  </Button>
                  
                  <Tooltip title={showExecution ? "Hide execution results" : "Show execution results"}>
                    <Button
                      icon={<BarChartOutlined />}
                      onClick={() => setShowExecution(!showExecution)}
                      size="small"
                      type={showExecution ? "primary" : "default"}
                    >
                      Results
                    </Button>
                  </Tooltip>
                </Space>
              </Panel>

              {/* Execution Status Panel */}
              {executionResults && (
                <Panel position="top-right">
                  <Alert
                    message={
                      executionResults.success 
                        ? (isExecutionAnimationComplete ? 'Execution completed successfully' : 'Executing graph...') 
                        : 'Execution failed'
                    }
                    description={
                      <div style={{ 
                        maxHeight: '200px', 
                        overflowY: 'auto',
                        wordBreak: 'break-word'
                      }}>
                        {executionResults.error || `Output: ${executionResults.finalState?.output || 'No output'}`}
                      </div>
                    }
                    type={
                      executionResults.success 
                        ? (isExecutionAnimationComplete ? 'success' : 'info') 
                        : 'error'
                    }
                    showIcon
                    style={{ width: 350, maxHeight: '300px' }}
                  />
                </Panel>
              )}

              {/* Graph Stats Panel */}
              <Panel position="bottom-left">
                <Card size="small" style={{ width: 300 }}>
                  <div className="text-sm text-gray-600">
                    {currentGraphId && (
                      <>
                        <Text strong style={{ color: '#1890ff' }}>
                          {savedGraphs.find(g => g.id === currentGraphId)?.name || 'Saved Graph'}
                        </Text>
                        <br />
                      </>
                    )}
                    <Text>Nodes: {nodes.length}</Text> | <Text>Edges: {edges.length}</Text>
                    {selectedNodes.length > 0 && (
                      <>
                        <br />
                        <Text style={{ color: '#1890ff', fontWeight: 500 }}>
                          ● Selected: {selectedNodes.length} node(s)
                        </Text>
                      </>
                    )}
                    {(() => {
                      const unlinkedCount = nodes.filter(node => isNodeUnlinked(node.id)).length;
                      return unlinkedCount > 0 ? (
                        <>
                          <br />
                          <Text style={{ color: '#ff4d4f', fontWeight: 500 }}>
                            ⚠ Unlinked: {unlinkedCount} node(s)
                          </Text>
                        </>
                      ) : null;
                    })()}
                    {hasUnsavedChanges && (
                      <>
                        <br />
                        <Text style={{ color: '#ff7a00', fontWeight: 500 }}>
                          ● Unsaved Changes
                        </Text>
                      </>
                    )}
                    {currentGraphId && !hasUnsavedChanges && (
                      <>
                        <br />
                        <Text style={{ color: '#52c41a', fontWeight: 500 }}>
                          ● Saved to MongoDB
                        </Text>
                      </>
                    )}
                    {executionResults?.timings && (
                      <>
                        <br />
                        <Text>
                          <ClockCircleOutlined /> {executionResults.timings.total}ms
                        </Text>
                        <br />
                        <Text>
                          <CheckCircleOutlined /> Status: {executionResults.success ? 'Success' : 'Failed'}
                        </Text>
                      </>
                    )}
                    {!readonly && (
                      <>
                        <br />
                        <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '8px' }}>
                          <div>💡 Tips:</div>
                          <div>• Click nodes to select</div>
                          <div>• Ctrl+Click for multi-select</div>
                          <div>• Delete key to remove selected</div>
                          <div>• Red dashed border = unlinked</div>
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              </Panel>

              {/* Node Info Panel */}
              {selectedNode && (
                <Panel position="bottom-right">
                  <Card 
                    title={selectedNode.data.label}
                    size="small"
                    style={{ width: 350 }}
                    extra={
                      <Button 
                        size="small" 
                        onClick={() => setSelectedNode(null)}
                      >
                        ×
                      </Button>
                    }
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Tag color="blue">{selectedNode.type}</Tag>
                        <Text type="secondary">{selectedNode.data.description}</Text>
                      </div>
                      
                      {selectedNode.data.config && (
                        <div>
                          <Text strong>Configuration:</Text>
                          <pre style={{ 
                            fontSize: '12px', 
                            background: '#f5f5f5', 
                            padding: '8px',
                            borderRadius: '4px',
                            margin: '4px 0'
                          }}>
                            {JSON.stringify(selectedNode.data.config, null, 2)}
                          </pre>
                        </div>
                      )}
                    </Space>
                  </Card>
                </Panel>
              )}
            </ReactFlow>
          </div>
        </div>

        {/* Execution Results Drawer */}
        <ExecutionPanel
          visible={showExecution}
          onClose={() => setShowExecution(false)}
          results={executionResults}
          history={executionHistory}
        />

        {/* Save Graph Modal */}
        <Modal
          title={currentGraphId ? "Update Graph" : "Save Graph"}
          open={showSaveModal}
          onOk={() => saveGraph(saveGraphName, saveGraphDescription)}
          onCancel={() => {
            setShowSaveModal(false);
            setSaveGraphName('');
            setSaveGraphDescription('');
          }}
          confirmLoading={isLoading}
          okText={currentGraphId ? "Update" : "Save"}
          style={{ zIndex: 2000 }}
          mask={true}
          maskClosable={false}
          destroyOnHidden={true}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <label>Graph Name:</label>
              <Input
                value={saveGraphName}
                onChange={(e) => setSaveGraphName(e.target.value)}
                placeholder="Enter graph name"
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              <label>Description (optional):</label>
              <Input.TextArea
                value={saveGraphDescription}
                onChange={(e) => setSaveGraphDescription(e.target.value)}
                placeholder="Enter graph description"
                rows={3}
                style={{ marginTop: 4 }}
              />
            </div>
          </Space>
        </Modal>

        {/* Load Graph Modal */}
        <Modal
          title="Load Graph"
          open={showLoadModal}
          onOk={loadGraph}
          onCancel={() => {
            setShowLoadModal(false);
            setSelectedGraphId('');
          }}
          confirmLoading={isLoading}
          okText="Load"
          okButtonProps={{ disabled: !selectedGraphId }}
          style={{ zIndex: 2000 }}
          mask={true}
          maskClosable={false}
          destroyOnHidden={true}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <label>Select Graph:</label>
              <Select
                value={selectedGraphId}
                onChange={setSelectedGraphId}
                placeholder="Choose a saved graph"
                style={{ width: '100%', marginTop: 4 }}
                showSearch
                optionFilterProp="children"
              >
                {savedGraphs.map((graph) => (
                  <Select.Option key={graph.id} value={graph.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>{graph.name}</div>
                        {graph.description && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {graph.description}
                          </div>
                        )}
                        <div style={{ fontSize: '11px', color: '#999' }}>
                          Updated: {new Date(graph.updatedAt).toLocaleDateString()} | 
                          Nodes: {graph.nodes?.length || 0} | 
                          Edges: {graph.edges?.length || 0}
                        </div>
                      </div>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          Modal.confirm({
                            title: 'Delete Graph',
                            content: `Are you sure you want to delete "${graph.name}"?`,
                            okText: 'Delete',
                            okType: 'danger',
                            onOk: () => deleteGraph(graph.id),
                          });
                        }}
                      />
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </div>
            {savedGraphs.length === 0 && (
              <Alert
                message="No saved graphs found"
                description="Create and save a graph first to load it later."
                type="info"
                showIcon
              />
            )}
          </Space>
        </Modal>

        {/* Node Configuration Modal */}
        <NodeConfigurationModal
          visible={showConfigModal}
          node={configNode}
          onClose={() => {
            setShowConfigModal(false);
            setConfigNode(null);
          }}
          onSave={handleNodeConfigSave}
          projectId={projectId}
        />

        {/* Input Collection Modal */}
        <Modal
          title="Workflow Input Required"
          open={showInputModal}
          onOk={handleInputSubmit}
          onCancel={handleInputCancel}
          okText="Execute Workflow"
          cancelText="Cancel"
          okButtonProps={{ disabled: inputCollectionData.some(input => !inputValues[input.nodeId]?.trim()) }}
          width={500}
          style={{ zIndex: 2000 }}
          mask={true}
          maskClosable={false}
          destroyOnHidden={true}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div style={{ marginBottom: '16px' }}>
              <Text type="secondary">
                Please provide the required inputs for your AI workflow:
              </Text>
            </div>
            
            {inputCollectionData.map((input, index) => (
              <div key={input.nodeId}>
                <label style={{ fontWeight: 500 }}>{input.label}:</label>
                {input.description && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', marginBottom: '6px' }}>
                    {input.description}
                  </div>
                )}
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  style={{ position: 'relative', zIndex: 2001 }}
                >
                  <Input.TextArea
                    value={inputValues[input.nodeId] || ''}
                    onChange={(e) => {
                      setInputValues(prev => ({
                        ...prev,
                        [input.nodeId]: e.target.value
                      }));
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      // Allow all keyboard events including delete, backspace, etc.
                    }}
                    onKeyUp={(e) => e.stopPropagation()}
                    onInput={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    placeholder={input.placeholder}
                    rows={3}
                    style={{ marginTop: 4 }}
                    autoFocus={index === 0}
                  />
                </div>
              </div>
            ))}
          </Space>
        </Modal>
      </ReactFlowProvider>
    </div>
  );
};

export default StateGraphEditor;